import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Send, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useReply, useChangeStatus } from "@/hooks/use-conversations-mutations";
import { useTemplates } from "@/hooks/use-templates";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_auth/responder")({
  component: ResponderPage,
});

const STATUS_OPTIONS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "en_proceso", label: "En proceso" },
  { value: "resuelto", label: "Resuelto" },
  { value: "derivado", label: "Derivado" },
  { value: "archivado", label: "Archivado" },
];

function ResponderPage() {
  const [active, setActive] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);

  const conversations = useQuery({
    queryKey: ["conversations"],
    queryFn: () => api.listConversations({ limit: 100 }),
  });

  const messages = useQuery({
    queryKey: ["messages", active],
    queryFn: () => api.listMessages(active!),
    enabled: active !== null,
  });

  const templates = useTemplates();
  const reply = useReply(active);
  const changeStatus = useChangeStatus();

  const activeConv = conversations.data?.find((c) => c.id === active);

  function sendMessage() {
    if (!draft.trim() || !active) return;
    reply.mutate({ text: draft }, { onSuccess: () => setDraft("") });
  }

  return (
    <div className="h-[calc(100vh-4rem)] grid grid-cols-[320px_1fr_280px]">
      {/* Lista conversaciones */}
      <aside className="border-r border-ink-200 dark:border-ink-800 overflow-auto">
        <div className="p-3 border-b border-ink-200 dark:border-ink-800 sticky top-0 bg-white dark:bg-ink-900 z-10">
          <h2 className="font-semibold text-ink-900 dark:text-white">Bandeja</h2>
        </div>
        {conversations.isLoading ? (
          <div className="p-3 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
          </div>
        ) : (
          conversations.data?.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={cn(
                "w-full text-left p-3 border-b border-ink-100 dark:border-ink-800 hover:bg-ink-50 dark:hover:bg-ink-800",
                active === c.id && "bg-brand-50 dark:bg-brand-900/30",
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-ink-900 dark:text-white truncate">
                  {c.contact?.display_name ?? c.contact?.handle ?? "Anónimo"}
                </span>
                <Badge variant={c.status === "resuelto" ? "good" : c.status === "pendiente" ? "warn" : "muted"}>
                  {c.status}
                </Badge>
              </div>
              <p className="text-xs text-ink-500 truncate">
                {c.network} · {c.unread_count > 0 ? `${c.unread_count} sin leer` : "leído"}
              </p>
            </button>
          ))
        )}
      </aside>

      {/* Thread */}
      <section className="flex flex-col">
        {active === null ? (
          <div className="flex-1 flex items-center justify-center text-ink-500">
            Selecciona una conversación
          </div>
        ) : (
          <>
            <div className="border-b border-ink-200 dark:border-ink-800 p-3 flex items-center gap-2 bg-white dark:bg-ink-900">
              <span className="text-sm font-medium text-ink-900 dark:text-white">
                {activeConv?.contact?.display_name ?? activeConv?.contact?.handle ?? "Conversación"}
              </span>
              <select
                value={activeConv?.status ?? "pendiente"}
                onChange={(e) => changeStatus.mutate({ id: active, status: e.target.value })}
                disabled={changeStatus.isPending}
                className="ml-auto h-8 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 text-xs"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-3 bg-ink-50 dark:bg-ink-950">
              {messages.isLoading ? (
                <Skeleton className="h-20" />
              ) : (
                messages.data?.map((m) => (
                  <div key={m.id} className={cn("flex", m.direction === "out" ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "max-w-[70%] rounded-2xl px-4 py-2 text-sm",
                        m.is_internal_note
                          ? "bg-warn/10 border border-warn/30 text-ink-900 dark:text-white"
                          : m.direction === "out"
                            ? "bg-brand-600 text-white"
                            : "bg-white dark:bg-ink-800 text-ink-900 dark:text-white shadow-card",
                      )}
                    >
                      {m.is_internal_note && <p className="text-[10px] font-semibold text-warn mb-1">NOTA INTERNA</p>}
                      <p>{m.text}</p>
                      <p className="text-[10px] opacity-70 mt-1">
                        {new Date(m.created_at).toLocaleTimeString("es-PE")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-ink-200 dark:border-ink-800 p-3 bg-white dark:bg-ink-900">
              <div className="flex gap-2 items-start">
                <Input
                  className="flex-1"
                  placeholder="Escribe tu respuesta…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && draft.trim()) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowTemplates((v) => !v)}
                  >
                    Plantillas <ChevronDown size={14} />
                  </Button>
                  {showTemplates && (
                    <div className="absolute right-0 bottom-12 w-72 max-h-72 overflow-auto bg-white dark:bg-ink-900 shadow-pop rounded-lg ring-soft z-20">
                      {templates.data && templates.data.length > 0 ? (
                        templates.data.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => {
                              setDraft(t.body);
                              setShowTemplates(false);
                            }}
                            className="w-full text-left p-3 hover:bg-ink-50 dark:hover:bg-ink-800 border-b border-ink-100 dark:border-ink-800 last:border-0"
                          >
                            <div className="text-sm font-medium text-ink-900 dark:text-white">{t.name}</div>
                            <div className="text-xs text-ink-500 line-clamp-2">{t.body}</div>
                          </button>
                        ))
                      ) : (
                        <p className="p-3 text-xs text-ink-500">Sin plantillas. Créalas en Configuración.</p>
                      )}
                    </div>
                  )}
                </div>
                <Button onClick={sendMessage} disabled={!draft.trim() || reply.isPending}>
                  <Send size={16} /> {reply.isPending ? "Enviando…" : "Enviar"}
                </Button>
              </div>
              <p className="text-[11px] text-ink-500 mt-2">
                Enter para enviar · Shift+Enter salto de línea · {templates.data?.length ?? 0} plantillas disponibles
              </p>
            </div>
          </>
        )}
      </section>

      <aside className="border-l border-ink-200 dark:border-ink-800 p-4 overflow-auto">
        {activeConv?.contact ? (
          <Card>
            <h3 className="font-semibold text-ink-900 dark:text-white text-sm mb-2">Contacto</h3>
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-ink-500">Nombre: </span>
                <span className="text-ink-900 dark:text-white">{activeConv.contact.display_name ?? "—"}</span>
              </div>
              <div>
                <span className="text-ink-500">Handle: </span>
                <span className="font-mono text-ink-900 dark:text-white">{activeConv.contact.handle}</span>
              </div>
              <div>
                <span className="text-ink-500">Red: </span>
                <Badge variant="muted">{activeConv.network}</Badge>
              </div>
            </div>
          </Card>
        ) : (
          <p className="text-xs text-ink-500">Selecciona una conversación para ver el contacto.</p>
        )}
      </aside>
    </div>
  );
}
